import { ABaseModel } from '../abstracts/ABaseModel.js';
import {
  IQuiz,
  IQuizValue,
  IQuizPage,
  IQuizPageListResult,
  IQuizQuestion,
  IQuizQuestionLocale,
  IQuizQuestionValidation,
  IQuizQuestionButton,
  IQuizQuestionOption,
  IQuizQuestionOptionFile,
  IQuizResponse,
  IQuizAnswer,
  IQuizProfile,
  IQuizProfileLocale,
  IQuizProfilePaginationResult,
  IQuizProfileArtist,
  IQuizProfileArtistListResult,
} from '../interfaces/Quiz.js';
import { Image } from './Image.js';
import { ListResult, PaginationResult } from './List.js';
import { CmsContentColumn } from './Cms.js';

/**
 * Quiz
 */
class Quiz extends ABaseModel implements IQuiz {
  id: number;
  name: string;
  description: string;
  averages: Array<QuizValue>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizValue
 */
class QuizValue extends ABaseModel implements IQuizValue {
  id: number;
  score: number;
  resultId: number;
  resultType: string;
  resultCode: string;
  resultName: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizPage
 */
class QuizPage extends ABaseModel implements IQuizPage {
  id: number;
  status: number;
  order: number;
  transition: string;
  name: string;
  questions: Array<QuizQuestion>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizPageListResult
 */
class QuizPageListResult extends ListResult implements IQuizPageListResult {
  declare items: Array<QuizPage>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizQuestionLocale
 */
class QuizQuestionLocale extends ABaseModel implements IQuizQuestionLocale {
  id: number;
  localeCode: string;
  text: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizQuestion
 */
class QuizQuestion extends ABaseModel implements IQuizQuestion {
  id: number;
  order: number;
  status: number;
  type: string;
  required: boolean;
  inputType: string;
  dataType: string;
  text: string;
  helper: string;
  validations: Array<QuizQuestionValidation>;
  buttons: Array<QuizQuestionButton>;
  images: Array<Image>;
  options: Array<QuizQuestionOption>;
  locales: Array<QuizQuestionLocale>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizQuestionValidation
 */
class QuizQuestionValidation extends ABaseModel implements IQuizQuestionValidation {
  type: string;
  config: string;
  message: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizQuestionButton
 */
class QuizQuestionButton extends ABaseModel implements IQuizQuestionButton {
  type: string;
  enabled: boolean;
  text: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizQuestionOption
 */
class QuizQuestionOption extends ABaseModel implements IQuizQuestionOption {
  id: number;
  status: number;
  type: string;
  name: string;
  text: string;
  file: QuizQuestionOptionFile;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizQuestionOptionFile
 */
class QuizQuestionOptionFile extends ABaseModel implements IQuizQuestionOptionFile {
  id: number;
  status: number;
  public: boolean;
  mimeType: string;
  key: string;
  extension: string;
  size: number;
  width: number;
  height: number;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizResponse
 */
class QuizResponse extends ABaseModel implements IQuizResponse {
  id: number;
  created: string;
  visitorId: number;
  userId: number;
  quiz: Quiz;
  profile: QuizProfile;
  values: Array<QuizValue>;
  answers: Array<QuizAnswer>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizAnswer
 */
class QuizAnswer extends ABaseModel implements IQuizAnswer {
  id: number;
  valueType: string;
  valueData: any;
  questionId: number;
  questionType: string;
  questionInputType: string;
  questionDataType: string;
  questionText: string;
  optionId: number;
  optionName: string;
  optionType: string;
  optionText: string;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizProfileLocale
 */
class QuizProfileLocale extends ABaseModel implements IQuizProfileLocale {
  id: number;
  localeCode: string;
  slug: string;
  name: string;
  hint: string;
  shortDescription: string;
  longDescription: Array<CmsContentColumn>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizProfile
 */
class QuizProfile extends ABaseModel implements IQuizProfile {
  id: number;
  type: string;
  code: string;
  slug: string;
  name: string;
  hint: string;
  shortDescription: string;
  longDescription: Array<CmsContentColumn>;
  images: Array<Image>;
  values: Array<QuizValue>;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  pageTitle: string;
  locales: Array<QuizProfileLocale>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizProfilePaginationResult
 */
class QuizProfilePaginationResult extends PaginationResult implements IQuizProfilePaginationResult {
  declare items: Array<QuizProfile>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizProfileArtist
 */
class QuizProfileArtist extends ABaseModel implements IQuizProfileArtist {
  id: number;
  alias: string;
  name: string;
  description: string;
  images: Array<Image>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

/**
 * QuizProfileArtistListResult
 */
class QuizProfileArtistListResult extends ListResult implements IQuizProfileArtistListResult {
  declare items: Array<QuizProfileArtist>;

  /**
   * constructor
   */
  constructor() {
    super();
  }
}

export {
  Quiz,
  QuizValue,
  QuizPage,
  QuizPageListResult,
  QuizQuestion,
  QuizQuestionLocale,
  QuizQuestionValidation,
  QuizQuestionButton,
  QuizQuestionOption,
  QuizQuestionOptionFile,
  QuizResponse,
  QuizAnswer,
  QuizProfile,
  QuizProfileLocale,
  QuizProfilePaginationResult,
  QuizProfileArtist,
  QuizProfileArtistListResult,
};
